import useFetch from "@/hook/useFetch/useFetch";
import { getMissionSurvey, saveMissionSurvey } from "@/services/mission/missions";
import { Model } from "survey-react-ui";
import { useState, useMemo, useEffect, useCallback } from "react";
import { LoaderContainer } from "@/pages/logged-page/style-component";
import { SpaceshipLoader } from "@/components/spaceship-loader/spaceship-loader";
import SurveyStyle from "@/pages/survey-page/style-component";
import { useTheme } from '@/contexts/theme-context/useThemeContext';
import { themeJsonLight } from '@/pages/survey-page/utils/theme-light';
import { themeJsonDark } from '@/pages/survey-page/utils/theme-dark';
import 'survey-core/defaultV2.min.css';
import { ContentSurveyStyled } from "./style-component";
import { Objective } from "@/types/objective/objective-type";
import ExpiredMission from "@/components/expired-mission/expired-mission";
import { ObjectiveComponentProps } from "../../missions-render-types";

interface RenderSurveyProps {
  objective: Objective;
  getProgress: () => Promise<void>;
  completedRequirements: boolean;
  isComingSoon: boolean;
  disabledMission: boolean;
}

const RenderSurvey: React.FC<RenderSurveyProps> = ({ objective, getProgress, completedRequirements, isComingSoon, disabledMission }) => {
  const { type } = objective;
  const svIdSurvey = type?.split(':')?.[1] ?? '';
  const idSurvey = Number(svIdSurvey?.split('_')?.[1] ?? 0);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { theme } = useTheme();
  const isDark = theme.name === 'dark';

  const { data, loading, error } = useFetch(getMissionSurvey, { id: idSurvey });
  const isLoad = loading || isLoading;
  const [errorMessage, setErrorMessage] = useState<string | null>(error?.message ?? null);
  const disable = !completedRequirements || isComingSoon || disabledMission;
  const { content } = data[0] ?? {};
  const survey = useMemo(() => new Model(content), [content]);
  const surveyTheme = isDark ? themeJsonDark : themeJsonLight;

  useEffect(() => {
    //@ts-expect-error - SurveyJS types don't properly define the applyTheme method
    survey.applyTheme(surveyTheme);
    survey.completeText = 'Enviar respuestas';
    survey.checkErrorsMode = 'onComplete';
    survey.mode = disable ? "display" : "edit";
  }, [survey, surveyTheme, disable]);

  const handleCompleteSurvey = useCallback(async (totalQuestions: number) => {
    try {
      setErrorMessage(null);
      setIsLoading(true);
      const body = {
        status: 1,
        content: content,
        correct_answer_count: totalQuestions,
        schema_id: idSurvey
      };
      await saveMissionSurvey(body);
      window.localStorage.setItem('is-sent-survey', 'true');
      await getProgress();
      window.localStorage.removeItem('is-sent-survey');
    } catch (error) {
      console.error(error);
      window.localStorage.removeItem('is-sent-survey');
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('Ocurrió un error inesperado');
      }
    } finally {
      setIsLoading(false);
    }
  }, [content, idSurvey, getProgress]);

  useEffect(() => {
    const validateHandler = () => {
      if (!completedRequirements || isComingSoon) {
        throw new Error('No se puede enviar las respuestas');
      }
    };

    const completeHandler = (sender: Model) => {
      const correctAnswersCount = sender.getAllQuestions().length - 1;
      handleCompleteSurvey(correctAnswersCount);
    };

    survey.onValidateQuestion.add(validateHandler);
    survey.onComplete.add(completeHandler);

    return () => {
      survey.onValidateQuestion.remove(validateHandler);
      survey.onComplete.remove(completeHandler);
    };
  }, [survey, completedRequirements, isComingSoon, handleCompleteSurvey]);


  if (errorMessage) {
    return <LoaderContainer>
      <p style={{ color: 'red' }}>{errorMessage}</p>
    </LoaderContainer>;
  }

  if (isLoad) {
    return <LoaderContainer>
      <SpaceshipLoader height="150px" />
    </LoaderContainer>;
  }

  return (
    <ContentSurveyStyled>
      <SurveyStyle model={survey} />
    </ContentSurveyStyled>
  );
};

const ObjectiveSurvey: React.FC<ObjectiveComponentProps> = ({ objective, isExpiredMission, isComingSoon, getProgress, isCompleted, completedRequirements, disabledMission }) => {

  const { participant_progress, quantity } = objective;
  const { current_progress = 0 } = participant_progress ?? {};
  const completed = (completedRequirements && isCompleted) || (current_progress && current_progress >= quantity);


  if (!isCompleted && isExpiredMission) {
    return (
      <ExpiredMission />
    );
  }

  if (completed) {
    return null;
  }

  return (
    <RenderSurvey 
      completedRequirements={completedRequirements}
      isComingSoon={isComingSoon}
      objective={objective}
      getProgress={getProgress}
      disabledMission={disabledMission}
    />
  );
};

export default ObjectiveSurvey;